import React, { useState } from 'react';
import { Modal, IconButton, Box, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';

import {
  GuideModalContainer,
  GuideModalHeader,
  GuideModalHeaderTitle,
  GuideModalBody,
  GuideSection,
  GuideSectionLabel,
  GuideText,
  GuideDivider,
} from './GroupLessonStyled';

const TABS = [
  { key: 'calendar', label: '캘린더' },
  { key: 'template', label: '그룹수업 관리' },
  { key: 'schedule', label: '스케줄 등록/수정' },
  { key: 'bulk', label: '스케줄 일괄수정' },
];

const CalendarGuide = () => (
  <>
    <GuideSection>
      <GuideSectionLabel>📅 캘린더</GuideSectionLabel>
      <GuideText>
        • 주간 단위로 그룹수업 스케줄을 확인합니다.<br />
        • 상단 <strong>강의실/강사 필터</strong>로 원하는 수업만 볼 수 있습니다.<br />
        • 수업 카드를 <strong>클릭</strong>하면 상세 정보와 예약 현황을 확인할 수 있습니다.<br />
        • 빈 시간대를 <strong>클릭</strong>하면 새 수업을 등록할 수 있습니다.<br />
        • <strong>시간 설정</strong> 버튼으로 캘린더 표시 시간 범위를 조정합니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>🎨 카드 표시</GuideSectionLabel>
      <GuideText>
        캘린더의 수업 카드는 상태에 따라 다르게 표시됩니다.<br /><br />
        <strong>카드 색상</strong><br />
        • <strong>기본(흰색)</strong> — 공개 상태의 일반 수업<br />
        • <strong>회색 배경</strong> — 비공개 수업 (회원앱에서 보이지 않음)<br /><br />
        <strong>상태 뱃지</strong><br />
        • <strong style={{ color: '#55C982' }}>초록 뱃지</strong> — 예약마감 (정원 도달)<br />
        • <strong style={{ color: '#FFB659' }}>주황 뱃지</strong> — 예약미달 (예약 있으나 정원 미달)<br />
        • <strong style={{ color: '#EE753A' }}>빨간 뱃지</strong> — 예약없음 (0명)<br /><br />
        <strong>기타 표시</strong><br />
        • <strong style={{ color: '#FFB74D' }}>N</strong> — 새로 등록된 수업 (잠시 후 자동으로 사라짐)<br />
        • 카드에 마우스를 올리면 상세 정보(강사, 강의실, 예약현황, 자동공개/폐강 설정)가 툴팁으로 표시됩니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>📊 예약 상태 필터</GuideSectionLabel>
      <GuideText>
        • <strong style={{ color: '#55C982' }}>예약마감</strong> — 예약 인원이 정원에 도달한 수업<br />
        • <strong style={{ color: '#FFB659' }}>예약미달</strong> — 예약이 있지만 정원에 미달한 수업<br />
        • <strong style={{ color: '#EE753A' }}>예약없음</strong> — 예약 인원이 0명인 수업<br /><br />
        상태 뱃지를 클릭하면 해당 상태의 수업만 필터링됩니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>📋 일정복사</GuideSectionLabel>
      <GuideText>
        현재 보고 있는 주의 전체 수업을 다음 주로 복사합니다.<br /><br />
        • 수업의 요일, 시간, 강사, 강의실, 정원, 수강권 등 모든 설정이 복사됩니다.<br />
        • <strong>예약 정보는 복사되지 않습니다.</strong> (빈 수업으로 생성)<br />
        • 대상 기간에 이미 수업이 있는 시간대와 충돌하면 복사가 실패합니다.<br />
        • 현재 주에 수업이 없으면 복사 버튼이 비활성화됩니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>🔒 비공개 수업</GuideSectionLabel>
      <GuideText>
        비공개 수업은 <strong>회원앱에서 보이지 않는</strong> 수업입니다.<br /><br />
        <strong>용도</strong><br />
        • 아직 확정되지 않은 수업을 미리 등록해두고, 확정 후 공개로 전환<br />
        • 자동 공개 설정과 함께 사용하여 특정 날짜/시간에 자동으로 공개<br />
        • 자동 폐강 조건에 해당되어 폐강 처리된 수업<br /><br />
        <strong>설정 방법</strong><br />
        • 스케줄 등록 시 각 수업의 공개/비공개 토글로 개별 설정<br />
        • 수업 수정에서 공개여부 변경<br />
        • 일괄수정에서 여러 수업의 공개여부를 한 번에 변경
      </GuideText>
    </GuideSection>
  </>
);

const TemplateGuide = () => (
  <>
    <GuideSection>
      <GuideSectionLabel>📋 그룹수업이란?</GuideSectionLabel>
      <GuideText>
        그룹수업은 수업의 기본 정보를 미리 정의해두는 <strong>템플릿</strong> 역할입니다.
        스케줄 등록 시 그룹수업을 선택하면 설정된 값이 자동으로 채워져 빠르게 등록할 수 있습니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>➕ 그룹수업 등록</GuideSectionLabel>
      <GuideText>
        • 그룹수업명, 수업명, 수강권, 수업시간, 차감횟수, 정원, 대기정원을 설정합니다.<br />
        • <strong>그룹수업명</strong>은 관리용 이름이고, <strong>수업명</strong>은 실제 캘린더에 표시되는 이름입니다.<br />
        • 등록된 그룹수업은 스케줄 등록 시 선택할 수 있습니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>✏️ 그룹수업 수정</GuideSectionLabel>
      <GuideText>
        그룹수업 정보를 수정하면서, 해당 그룹수업으로 등록된 <strong>기존 수업에도 변경사항을 일괄 적용</strong>할 수 있습니다.<br /><br />
        <strong>연결된 수업 적용</strong><br />
        • 수정 화면 하단에 그룹수업이 연결된 <strong>앞으로 진행될 수업 목록</strong>이 표시됩니다.<br />
        • 체크박스로 적용할 수업을 선택한 후 수정하면, 선택한 수업에만 변경사항이 반영됩니다.<br />
        • 수업을 선택하지 않으면 그룹수업 정보만 수정되고 기존 수업은 변경되지 않습니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>🗑️ 그룹수업 삭제</GuideSectionLabel>
      <GuideText>
        • 그룹수업을 삭제해도 <strong>이미 등록된 수업은 삭제되지 않습니다.</strong><br />
        • 연결된 수업이 있는 경우 삭제 확인 시 안내 메시지가 표시됩니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>⚠️ 주의사항</GuideSectionLabel>
      <GuideText>
        • 정원을 변경할 때 선택된 수업 중 <strong>현재 예약 인원보다 적은 값은 설정할 수 없습니다.</strong><br />
        • 대기 정원도 마찬가지로 현재 대기 인원보다 적게 설정할 수 없습니다.
      </GuideText>
    </GuideSection>
  </>
);

const ScheduleGuide = () => (
  <>
    <GuideSection>
      <GuideSectionLabel>✏️ 스케줄 등록</GuideSectionLabel>
      <GuideText>
        캘린더에서 빈 시간대를 클릭하거나, 그룹수업 관리에서 스케줄 등록 버튼으로 진입합니다.<br /><br />
        <strong>등록 순서</strong><br />
        • 그룹수업 선택 → 수업 기간 설정 → 요일/시간 추가 → 강사/강의실 지정 → 등록<br /><br />
        <strong>스케줄 설정</strong><br />
        • 한 번에 여러 요일/시간대를 추가할 수 있습니다. (예: 월·수·금 10:00~10:50)<br />
        • 각 스케줄 행마다 <strong>강사, 강의실</strong>을 개별 지정할 수 있습니다.<br />
        • 수업별로 <strong>공개/비공개</strong> 토글을 개별 설정할 수 있습니다.<br />
        • 같은 시간에 같은 강사가 겹치면 충돌 에러가 표시됩니다. 강의실은 센터설정의 <strong>동시간 최대 수업</strong> 수를 넘으면 막힙니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>🔄 수업 수정</GuideSectionLabel>
      <GuideText>
        캘린더에서 수업 카드를 클릭한 후 수정 버튼으로 개별 수업을 수정합니다.<br /><br />
        <strong>수정 가능 항목</strong><br />
        • 수업명, 강사, 강의실, 시간, 정원, 대기정원, 수강권, 공개여부, 메모<br /><br />
        <strong>시간/요일 변경 시</strong><br />
        • <strong>이 수업만 변경</strong> — 선택한 수업 1건만 변경됩니다.<br />
        • <strong>이후 수업 모두 변경</strong> — 선택한 수업 이후의 같은 스케줄 수업이 모두 변경됩니다.<br />
        • 예약이 있는 수업의 시간을 변경하면 경고가 표시됩니다.<br /><br />
        <strong>제한 사항</strong><br />
        • 정원은 현재 예약 인원보다 적게, 대기 정원은 현재 대기 인원보다 적게 설정할 수 없습니다.<br />
        • 같은 시간에 같은 강사가 겹치면 충돌 에러가 발생합니다. 강의실은 센터설정의 <strong>동시간 최대 수업</strong> 수를 넘으면 막힙니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>❌ 수업 취소</GuideSectionLabel>
      <GuideText>
        캘린더에서 수업 카드를 클릭한 후 취소 버튼으로 수업을 취소합니다.<br /><br />
        <strong>취소 방식</strong><br />
        • <strong>이 수업만 취소</strong> — 선택한 수업 1건만 취소됩니다.<br />
        • <strong>이후 수업 모두 취소</strong> — 선택한 수업부터 이후 같은 스케줄 수업이 모두 취소됩니다.<br /><br />
        <strong>주의사항</strong><br />
        • <strong>예약이 있는 수업은 취소할 수 없습니다.</strong> 예약을 먼저 취소한 후 수업을 취소해주세요.<br />
        • 취소된 수업은 캘린더에서 사라지며, 복구할 수 없습니다.<br />
        • 여러 수업을 한 번에 취소하려면 <strong>스케줄 일괄수정</strong>의 일괄취소 기능을 이용하세요.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>🔔 자동 공개 설정</GuideSectionLabel>
      <GuideText>
        • 설정 시 수업이 <strong>비공개로 생성</strong>되며, 지정한 날짜/시간에 자동으로 공개됩니다.<br />
        • 공개 날짜는 수업일 이전 날짜만 선택 가능합니다.<br />
        • 자동 공개를 해제하면 수업이 즉시 <strong>공개 상태</strong>로 전환됩니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>🚫 자동 폐강 설정</GuideSectionLabel>
      <GuideText>
        • 수업 시작 N시간 전에 예약 인원이 기준 미달이면 자동으로 폐강(비공개) 처리됩니다.<br />
        • <strong>폐강 시점</strong> — 수업 시작 몇 시간 전에 판단할지 설정<br />
        • <strong>폐강 조건</strong> — 예약 인원이 N명 이하이면 폐강
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>👥 예약/출석 관리</GuideSectionLabel>
      <GuideText>
        수업 카드를 클릭하면 예약 현황을 확인하고 관리할 수 있습니다.<br /><br />
        <strong>예약 상태</strong><br />
        • <strong>예약</strong> — 정원 내 정상 예약된 회원<br />
        • <strong>대기</strong> — 정원 초과 시 대기열에 등록된 회원. 취소 발생 시 자동으로 예약 전환됩니다.<br />
        • <strong>출석</strong> — 수업에 참석한 회원 (수강권 차감)<br />
        • <strong>결석</strong> — 수업에 불참한 회원 (수강권 차감)<br />
        • <strong>취소</strong> — 예약을 취소한 회원<br /><br />
        <strong>제한 사항</strong><br />
        • 정원을 현재 예약 인원보다 적게 변경할 수 없습니다.<br />
        • 대기 정원을 현재 대기 인원보다 적게 변경할 수 없습니다.
      </GuideText>
    </GuideSection>
  </>
);

const BulkGuide = () => (
  <>
    <GuideSection>
      <GuideSectionLabel>📑 스케줄 일괄수정이란?</GuideSectionLabel>
      <GuideText>
        여러 수업을 한 번에 수정하거나 취소할 때 사용합니다.<br /><br />
        <strong>사용 방법</strong><br />
        • 기간을 설정하고 검색 → 수업 목록에서 대상 수업 체크 → 일괄수정 또는 일괄취소
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>✏️ 일괄수정</GuideSectionLabel>
      <GuideText>
        <strong>변경 가능 항목</strong><br />
        • 수업명, 강사, 강의실, 시간, 날짜, 정원, 대기정원, 차감횟수, 수강권, 공개여부<br /><br />
        • 변경할 항목만 입력하면 해당 항목만 변경됩니다. (빈 항목은 기존 값 유지)<br />
        • 수정 전 변경 내역 요약이 표시되며, 확인 후 적용됩니다.<br />
        • 정원은 선택된 수업 중 현재 예약 인원보다 적게 설정할 수 없습니다.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>❌ 일괄취소</GuideSectionLabel>
      <GuideText>
        • 선택한 수업을 한 번에 취소합니다.<br />
        • <strong>예약이 있는 수업은 취소할 수 없습니다.</strong> 예약을 먼저 취소해주세요.<br />
        • 취소된 수업은 복구할 수 없으므로 신중하게 사용하세요.
      </GuideText>
    </GuideSection>

    <GuideDivider />

    <GuideSection>
      <GuideSectionLabel>🔍 필터</GuideSectionLabel>
      <GuideText>
        검색 결과에서 원하는 수업만 골라볼 수 있습니다.<br /><br />
        • <strong>수업명</strong> — 텍스트 검색<br />
        • <strong>강사 / 강의실 / 요일</strong> — 드롭다운 선택<br />
        • <strong>시간대</strong> — 시작~종료 시간 범위 지정<br />
        • <strong>상태</strong> — 예약마감, 예약미달, 예약없음, 비공개, 종료<br />
        • <strong>"종료된 수업 제외"</strong> — 이미 지난 수업을 목록에서 숨깁니다.<br />
        • 과거 기간을 검색하면 자동으로 종료된 수업도 표시됩니다.
      </GuideText>
    </GuideSection>
  </>
);

const TAB_CONTENT = {
  calendar: CalendarGuide,
  template: TemplateGuide,
  schedule: ScheduleGuide,
  bulk: BulkGuide,
};

const GroupLessonGuideModal = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState('calendar');
  const TabContent = TAB_CONTENT[activeTab];

  return (
    <Modal open={open} onClose={onClose} disableScrollLock>
      <GuideModalContainer>
        <GuideModalHeader>
          <GuideModalHeaderTitle>그룹수업 가이드</GuideModalHeaderTitle>
          <IconButton onClick={onClose} sx={{ color: '#fff', p: '4px' }}>
            <Close />
          </IconButton>
        </GuideModalHeader>

        {/* 탭 */}
        <Box sx={{
          display: 'flex', borderBottom: '1px solid #e0e0e0', flexShrink: 0, backgroundColor: '#fafafa',
        }}>
          {TABS.map(tab => (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              sx={{
                flex: 1, textAlign: 'center', padding: '12px 0', cursor: 'pointer',
                fontSize: '13px', fontWeight: activeTab === tab.key ? 700 : 500,
                fontFamily: 'Pretendard, sans-serif',
                color: activeTab === tab.key ? '#4AB3BC' : '#888',
                borderBottom: activeTab === tab.key ? '2px solid #4AB3BC' : '2px solid transparent',
                transition: 'all 0.15s ease',
                '&:hover': { color: '#4AB3BC', backgroundColor: '#f0f8f8' },
              }}
            >
              {tab.label}
            </Box>
          ))}
        </Box>

        <GuideModalBody>
          <TabContent />
        </GuideModalBody>
      </GuideModalContainer>
    </Modal>
  );
};

export default GroupLessonGuideModal;
